import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Cpu, Zap, CheckCircle2, XCircle } from "lucide-react";

const PROVIDERS = [
  { value: "anthropic", label: "Anthropic (Claude)" },
  { value: "openai", label: "OpenAI (GPT)" },
  { value: "gemini", label: "Google (Gemini)" },
  { value: "openai_compatible", label: "OpenAI-compatible (custom)" },
];

const MODELS = {
  openai: ["gpt-5.5", "gpt-5.4", "gpt-5.4-mini", "gpt-5.2", "gpt-4o", "gpt-4.1"],
  anthropic: ["claude-opus-4-8", "claude-opus-4-7", "claude-sonnet-4-6", "claude-haiku-4-5-20251001"],
  gemini: ["gemini-3.5-flash", "gemini-3.1-pro-preview", "gemini-3-flash-preview", "gemini-2.5-pro", "gemini-2.5-flash"],
  openai_compatible: [],
};

export default function AdminLlmSettings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [source, setSource] = useState("env");
  const [cfg, setCfg] = useState({ provider: "anthropic", model: "", base_url: "", api_key: "", fallback_provider: "openai", fallback_model: "" });

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/llm-config");
        setCfg({ provider: data.provider, model: data.model, base_url: data.base_url || "", api_key: "", fallback_provider: data.fallback_provider || "openai", fallback_model: data.fallback_model || "" });
        setHasApiKey(data.has_api_key);
        setSource(data.source);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/llm-config", cfg);
      toast.success("LLM settings saved.");
      setSource("db");
      if (cfg.api_key) setHasApiKey(true);
      set("api_key", "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/llm-config/test");
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: formatApiError(e.response?.data?.detail) || "Test failed." });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" /></div>;
  if (error) return (
    <div className="bg-white border border-zinc-200 rounded-xl py-20 text-center" data-testid="llm-error">
      <p className="text-lg font-semibold text-zinc-900">{error}</p>
      <p className="text-sm text-muted-foreground mt-1">You don't have permission to view this page.</p>
    </div>
  );

  const isCompat = cfg.provider === "openai_compatible";
  const modelList = MODELS[cfg.provider] || [];
  const fbModelList = MODELS[cfg.fallback_provider] || [];

  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-llm-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
          <Cpu className="h-7 w-7 text-accent" strokeWidth={1.75} /> LLM Settings
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Switch the model powering CV analysis & tailoring — applies instantly, no redeploy.
          {" "}Currently loaded from <span className="font-medium">{source === "db" ? "the dashboard (DB)" : ".env defaults"}</span>.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Primary model</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Provider</Label>
            <Select value={cfg.provider} onValueChange={(v) => { set("provider", v); set("model", ""); }}>
              <SelectTrigger data-testid="llm-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Model</Label>
            <Input list="model-list" value={cfg.model} onChange={(e) => set("model", e.target.value)} placeholder="e.g. claude-sonnet-4-6" data-testid="llm-model" />
            <datalist id="model-list">{modelList.map((m) => <option key={m} value={m} />)}</datalist>
          </div>
        </div>

        {isCompat && (
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-zinc-100">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Base URL</Label>
              <Input value={cfg.base_url} onChange={(e) => set("base_url", e.target.value)} placeholder="https://openrouter.ai/api/v1" data-testid="llm-base-url" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">API Key</Label>
              <Input type="password" value={cfg.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder={hasApiKey ? "•••••• (leave blank to keep)" : "sk-..."} data-testid="llm-api-key" />
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Fallback model <span className="normal-case font-normal">(used if the primary errors)</span></p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Provider</Label>
            <Select value={cfg.fallback_provider} onValueChange={(v) => { set("fallback_provider", v); set("fallback_model", ""); }}>
              <SelectTrigger data-testid="llm-fallback-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{PROVIDERS.filter((p) => p.value !== "openai_compatible").map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Model</Label>
            <Input list="fb-model-list" value={cfg.fallback_model} onChange={(e) => set("fallback_model", e.target.value)} placeholder="e.g. gpt-5.4" data-testid="llm-fallback-model" />
            <datalist id="fb-model-list">{fbModelList.map((m) => <option key={m} value={m} />)}</datalist>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="llm-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Save settings
        </Button>
        <Button variant="outline" onClick={runTest} disabled={testing} data-testid="llm-test-btn">
          {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />} Test connection
        </Button>
      </div>

      {testResult && (
        <div className={`rounded-lg border p-4 text-sm ${testResult.ok ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="llm-test-result">
          {testResult.ok ? (
            <p className="flex items-center gap-2 text-green-800"><CheckCircle2 className="h-4 w-4" /> Success — {testResult.provider}/{testResult.model} replied "{testResult.reply}" in {testResult.latency_ms}ms.</p>
          ) : (
            <p className="flex items-center gap-2 text-red-800"><XCircle className="h-4 w-4" /> Failed{testResult.provider ? ` (${testResult.provider}/${testResult.model})` : ""}: {testResult.error}</p>
          )}
        </div>
      )}
    </div>
  );
}
